export type ParticipantRole = 'officer' | 'caller' | 'unknown';

/**
 * LiveKit participant kinds (ParticipantInfo_Kind), assigned by the LiveKit server and compared as numbers rather than
 * by importing the protocol enum, so this stays usable from plain unit tests and from the `any`-typed LiveKit data
 * callbacks, where `kind` arrives as a number.
 */
/** A human browser seat minted by connection-details (ParticipantInfo_Kind.STANDARD). */
const STANDARD_KIND = 0;
/** The egress recorder (ParticipantInfo_Kind.EGRESS). */
export const EGRESS_KIND = 2;
const SIP_KIND = 3;
/** A dispatched agent (ParticipantInfo_Kind.AGENT). */
export const AGENT_KIND = 4;

/**
 * The pattern an officer-assist dispatch name is recognised by: `officer-assist` followed by up to 40 of [a-z0-9-] (so
 * the documented blue/green `officer-assist-g<N>` matches, and so do other suffixes). It is the same regex as agent-backend
 * `utils/trusted_participant.py` and agent-caller `officer_dispatch_routing.py`; tighten all three together if ever.
 * The browser recognises officer-assist by this pattern on the attested `lk.agent.name`, so an
 * OFFICER_ASSIST_AGENT_NAME outside it would not be recognised as officer-assist anywhere.
 */
export const OFFICER_ASSIST_DISPATCH_NAME_PATTERN = /^officer-assist[a-z0-9-]{0,40}$/;

export function parseParticipantMetadata(metadata?: string | null): Record<string, unknown> | null {
  if (!metadata) {
    return null;
  }

  try {
    const parsed = JSON.parse(metadata);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * The role connection-details minted into a browser identity, read from the LAST `__` segment ONLY.
 *
 * Browser identities are `<typed name>__<off|cit>_<postfix>`. Everything before the last `__` is whatever the
 * person typed, so `Jane__off_x__cit_ab12` is a CALLER (the same rule as agent-backend's `_server_minted_role`).
 * connection-details also refuses `__` in a typed name (Q-765), so a well-formed identity has exactly one marker.
 */
/**
 * Identity prefixes no browser seat may take: connection-details refuses a typed name that starts with one (compared in
 * lower case), so an identity that starts with one is not a connection-details mint and its `__off_`/`__cit_` text is not
 * read for a role. `sip_` is LiveKit's phone-leg default (the caller chooses the rest), `eg_` egress, `ingress` an
 * ingress, the rest our agents. THE one list (Q-153 r8, audit A7): connection-details imports it, and agent-backend's
 * copy is pinned to the same `agent-backend/demo/utils/reserved_identity_prefixes.v1.json` by a parity test in each suite.
 */
export const RESERVED_PARTICIPANT_IDENTITY_PREFIXES = [
  'bh-',
  'bithuman-',
  'agent-',
  'transcriber',
  'analyzer',
  'lk-',
  'sip_',
  'eg_',
  'ingress',
  'officer-avatar-',
] as const;

export function hasReservedParticipantIdentityPrefix(identity: string): boolean {
  const lowered = identity.toLowerCase();
  return RESERVED_PARTICIPANT_IDENTITY_PREFIXES.some((prefix) => lowered.startsWith(prefix));
}

export function serverMintedRole(identity?: string | null): 'officer' | 'caller' | null {
  if (typeof identity !== 'string' || identity.length === 0) return null;
  // Not a connection-details mint: LiveKit names a phone leg `sip_<From user>` and the CALLER chooses the From user, so
  // `sip_x__off_y` is a forgery; likewise every other reserved prefix (Q-153 r8, audit A7).
  if (hasReservedParticipantIdentityPrefix(identity)) return null;
  const separator = identity.lastIndexOf('__');
  if (separator <= 0) return null;
  const suffix = identity.slice(separator + 2).toLowerCase();
  if (/^off_./.test(suffix)) return 'officer';
  if (/^cit_./.test(suffix)) return 'caller';
  return null;
}

/**
 * The ROLE-INVITE seat connection-details minted into a browser identity (room controls C5, CONTROLS.md §3), read from
 * the same last `__` segment as `serverMintedRole`, so it is as attested as the role (a typed name cannot contain `__`):
 *   `cit_gst_<g seat>_<postfix>`  an external guest (civilian side: `serverMintedRole` reads it as `caller` so audio,
 *                                  captions and the civilian count work; every caller-only path must exclude it);
 * Null for any other identity, including every ordinary caller (`cit_<postfix>`) and officer (`off_<postfix>`).
 */
export function serverMintedInviteSeat(identity?: string | null): { party: 'guest'; seat: string } | null {
  if (serverMintedRole(identity) === null) return null;
  const suffix = (identity as string).slice((identity as string).lastIndexOf('__') + 2);
  const guest = /^cit_gst_(g[1-9][0-9]*)_[a-z0-9]{4}$/.exec(suffix);
  if (guest) return { party: 'guest', seat: guest[1] };
  return null;
}

/** Is this identity a server-minted external GUEST seat (C5)? */
export function isServerMintedGuest(identity?: string | null): boolean {
  return serverMintedInviteSeat(identity)?.party === 'guest';
}

/**
 * An officer-side seat filled from a role invite (room controls C7/C8): `<name>__off_sup_<s seat>_<postfix>` support
 * staff of the call's organisation, `<name>__off_spc_<o seat>_<postfix>` an officer of another agency. Read from the
 * server-minted last segment, like the role itself. Null for any other identity.
 */
export function serverMintedStaffSeat(identity?: string | null): { kind: 'support' | 'specialist'; seat: string } | null {
  if (serverMintedRole(identity) !== 'officer') return null;
  const suffix = (identity as string).slice((identity as string).lastIndexOf('__') + 2);
  const support = /^off_sup_(s[1-9][0-9]*)_[a-z0-9]{4}$/.exec(suffix);
  if (support) return { kind: 'support', seat: support[1] };
  const specialist = /^off_spc_(o(?:[2-9]|[1-9][0-9]+))_[a-z0-9]{4}$/.exec(suffix);
  if (specialist) return { kind: 'specialist', seat: specialist[1] };
  return null;
}

/**
 * Is this identity a server-minted PARTNER officer seat (R1021-023)? connection-details mints `<name>__off_ptr_<officer tag>_<postfix>`
 * for an officer of the call's organisation who joins a call another officer leads. Read from the same last `__`
 * segment as `serverMintedRole`, so it is as attested as the officer role itself; the seat is an officer seat on every
 * path (`off_` prefix). An officer seat without the marker is shown as the lead's: the owner, or (rarely) staff with no
 * resolvable organisation, who join without a scope claim at all (an older path this marker does not change).
 */
export function isServerMintedPartnerOfficer(identity?: string | null): boolean {
  if (serverMintedRole(identity) !== 'officer') return false;
  const suffix = (identity as string).slice((identity as string).lastIndexOf('__') + 2);
  return /^off_ptr_[0-9a-f]{10}_[a-z0-9]{4}$/.test(suffix);
}

/** Fields of a LiveKit participant the role can be derived from (a `Participant` satisfies it). */
export interface RoleParticipant {
  identity?: string | null;
  /** Never read for the role: a display name is a request parameter (kept so call sites can pass a participant). */
  name?: string | null;
  /** Read only for an AGENT, whose metadata our own worker writes; a human may rewrite theirs (canUpdateOwnMetadata). */
  metadata?: string | null;
  kind?: unknown;
  attributes?: Record<string, string> | null;
}

/**
 * Officer-assist, recognised ONLY from what LiveKit attests: kind AGENT and the dispatch name it stamps in
 * `lk.agent.name`. Never from an identity substring, a display name or metadata (a person can type
 * "officer-assist"). The ONE predicate for any decision that acts on officer-assist (Q-153 r6, security C2).
 */
export function isAttestedOfficerAssist(participant?: { kind?: unknown; attributes?: Record<string, string> | null } | null): boolean {
  if (!participant || participant.kind !== AGENT_KIND) return false;
  return OFFICER_ASSIST_DISPATCH_NAME_PATTERN.test(participant.attributes?.['lk.agent.name'] ?? '');
}

/** The AI officer's bitHuman avatar joins under this one fixed identity (Q-1114; prefix reserved above). */
export const OFFICER_VOICE_AVATAR_IDENTITY = 'officer-avatar-agent';
/** officer-assist's OWN attribute saying its avatar carries its voice right now (`active`) or not (`off`). */
export const OFFICER_AVATAR_STATE_ATTRIBUTE = 'officer_avatar';
export const OFFICER_AVATAR_ACTIVE = 'active';

/**
 * The HUMAN officer's bitHuman avatar (Q-1724) joins under this one fixed identity (same reserved prefix: no seat can mint
 * it). It voices the officer's OWN microphone and holds no role.
 */
export const HUMAN_OFFICER_AVATAR_IDENTITY = 'officer-avatar-human';
/** officer-assist's OWN attribute saying the human officer's avatar is live (`active`) or not (`off`). */
export const HUMAN_OFFICER_AVATAR_STATE_ATTRIBUTE = 'human_officer_avatar';
/** officer-assist's OWN attribute naming the officer SEAT identity the avatar stands in for. */
export const HUMAN_OFFICER_AVATAR_FOR_ATTRIBUTE = 'human_officer_avatar_for';
export const HUMAN_OFFICER_AVATAR_ACTIVE = 'active';
/** officer-assist's OWN attribute: the sequence number of the utterance the human officer's avatar carries right now ('' = none). */
export const HUMAN_OFFICER_AVATAR_UTTERANCE_ATTRIBUTE = 'human_officer_avatar_utt';

/**
 * The AI officer's bitHuman avatar by its reserved identity, live, inactive or orphaned (Q-1114). An EXCLUSION only:
 * it is never a caller, a citizen or a call party. It grants nothing; `isOfficerVoiceAvatar` is the voice predicate.
 */
export function isOfficerAvatarAgent(participant?: { identity?: string | null; kind?: unknown } | null): boolean {
  return (
    participant?.kind === AGENT_KIND &&
    (participant.identity === OFFICER_VOICE_AVATAR_IDENTITY ||
      participant.identity === HUMAN_OFFICER_AVATAR_IDENTITY ||
      isOfficerSeatAvatarIdentity(participant.identity))
  );
}

/**
 * Every officer gets an avatar (R1021-026, #1882). The officer whose face the AI officer's voice wears keeps the Q-1114 avatar;
 * every OTHER officer seat gets its own avatar under this prefix (inside the reserved `officer-avatar-` prefix) plus 16 hex of
 * sha256(seat identity). It holds no role and carries no voice. officer-assist writes its OWN attribute `officer_avatars`, a JSON
 * object {avatar identity: officer seat identity}, after each avatar's verified join.
 */
export const OFFICER_SEAT_AVATAR_IDENTITY_PREFIX = `${OFFICER_VOICE_AVATAR_IDENTITY}--`;
export const OFFICER_AVATAR_SEATS_ATTRIBUTE = 'officer_avatars';

export function isOfficerSeatAvatarIdentity(identity?: string | null): boolean {
  return (
    typeof identity === 'string' &&
    identity.startsWith(OFFICER_SEAT_AVATAR_IDENTITY_PREFIX) &&
    identity.length > OFFICER_SEAT_AVATAR_IDENTITY_PREFIX.length
  );
}

/**
 * The officer seat a partner officer's avatar shows, or null (R1021-026). Valid only when LiveKit attests all of it: kind AGENT;
 * an identity under the seat-avatar prefix; `lk.publish_on_behalf` names a PRESENT attested officer-assist whose OWN
 * `officer_avatars` map lists this identity; and the seat it maps to is a server-minted officer seat PRESENT as a STANDARD
 * participant. Never read from the avatar's own token, name or metadata. Same contract as agent-backend
 * `officer_seat_avatar_seat` and agent-caller's copy, pinned by agent-backend/demo/utils/officer_seat_avatar.v1.json.
 */
export function officerSeatAvatarSeat(
  participant: { identity?: string | null; kind?: unknown; attributes?: Record<string, string> | null } | null | undefined,
  participants: Iterable<{ identity?: string | null; kind?: unknown; attributes?: Record<string, string> | null }>,
): string | null {
  if (!participant || participant.kind !== AGENT_KIND || !isOfficerSeatAvatarIdentity(participant.identity)) return null;
  const principalIdentity = participant.attributes?.['lk.publish_on_behalf'];
  if (!principalIdentity || principalIdentity === participant.identity) return null;
  const seats = Array.from(participants);
  for (const candidate of seats) {
    if (candidate?.identity !== principalIdentity || !isAttestedOfficerAssist(candidate)) continue;
    let mapping: unknown;
    try {
      mapping = JSON.parse(candidate.attributes?.[OFFICER_AVATAR_SEATS_ATTRIBUTE] || '{}');
    } catch {
      continue;
    }
    if (!mapping || typeof mapping !== 'object' || Array.isArray(mapping)) continue;
    const seat = (mapping as Record<string, unknown>)[participant.identity as string];
    if (typeof seat !== 'string' || serverMintedRole(seat) !== 'officer') continue;
    if (seats.some((other) => other?.identity === seat && other.kind === STANDARD_KIND)) return seat;
  }
  return null;
}

/**
 * The officer-avatar map (`officer_avatars`) an attested officer-assist publishes on ITSELF while its voice avatar is
 * `active` (R1021-026), or null: not an attested officer-assist, the avatar not active, or the map malformed. Display code
 * only; kept here so the role-derivation ratchet's only attribute reader stays this helper file.
 */
export function activeOfficerAvatarMap(
  candidate: { kind?: unknown; attributes?: Record<string, string> | null } | null | undefined,
): Record<string, unknown> | null {
  if (!candidate || !isAttestedOfficerAssist(candidate)) return null;
  if (candidate.attributes?.[OFFICER_AVATAR_STATE_ATTRIBUTE] !== OFFICER_AVATAR_ACTIVE) return null;
  let mapping: unknown;
  try {
    mapping = JSON.parse(candidate.attributes?.[OFFICER_AVATAR_SEATS_ATTRIBUTE] || '{}');
  } catch {
    return null;
  }
  if (!mapping || typeof mapping !== 'object' || Array.isArray(mapping)) return null;
  return mapping as Record<string, unknown>;
}

/** A phone leg: LiveKit names it `sip_<From user>` (a fact of the leg; the rest of the identity is the caller's). */
export function isPhoneLegIdentity(identity: string | null | undefined): boolean {
  return typeof identity === 'string' && identity.toLowerCase().startsWith('sip_');
}

/**
 * Is this identity a server-minted SUPPORT-STAFF seat (`<name>__off_sup_<s seat>_<postfix>`, room controls C7)? Staff, not an
 * officer, wherever a view picks officers: never an officer tile, never the main officer (Batch 5).
 */
export function isServerMintedSupportStaff(identity?: string | null): boolean {
  return serverMintedStaffSeat(identity)?.kind === 'support';
}

/**
 * The seat roles a join notice is shown to (everyone but an officer). THE one list: the notice ticket reads it, so a ticket
 * cannot name a role this list does not (Batch 5, ratchet).
 */
export const NON_OFFICER_SEAT_ROLES = ['caller', 'guest', 'interpreter', 'observer', 'phone'] as const;
export type NonOfficerSeatRole = (typeof NON_OFFICER_SEAT_ROLES)[number];

export function isNonOfficerSeatRole(value: unknown): value is NonOfficerSeatRole {
  return typeof value === 'string' && (NON_OFFICER_SEAT_ROLES as readonly string[]).includes(value);
}

/**
 * Which side of the call a control TARGET seat role is on (CONTROLS.md section 6): a caller, guest or observer is a civilian;
 * a partner, specialist, support or trainee seat is officer side; anything else (the lead, a supervisor) is neither.
 */
export function seatTargetSide(role: string): 'civilian' | 'officer_side' | null {
  if (role === 'caller' || role === 'guest' || role === 'observer') return 'civilian';
  if (role === 'partner' || role === 'specialist' || role === 'support' || role === 'trainee') return 'officer_side';
  return null;
}

/** The role a remembered seat record (stored JSON) carries: only the stored value 'observer' is an observer; anything else is a caller. */
export function rememberedSeatRole(record: { role?: unknown } | null | undefined): 'observer' | 'caller' {
  return record?.role === 'observer' ? 'observer' : 'caller';
}

/**
 * Is this the AI officer's bitHuman avatar, carrying officer-assist's voice right now (Q-1114)? NOT a role: the avatar
 * runs on the vendor's renderer, so getParticipantRole leaves it 'unknown' and it holds no officer or caller right. This
 * is for display and voice code only (the call sites are a ratchet, officerVoiceAvatar.callsites.test.ts).
 *
 * True only when LiveKit attests all of it: kind AGENT; the exact identity; `lk.publish_on_behalf` (minted into the
 * avatar's token by officer-assist) names a participant who is PRESENT and is an attested officer-assist; and that
 * officer-assist's own `officer_avatar` attribute is `active`. An orphan, an inactive avatar or a self-claim is false.
 * Same contract as agent-backend `is_officer_voice_avatar` and agent-caller's copy, pinned by
 * agent-backend/demo/utils/officer_voice_avatar.v1.json.
 */
export function isOfficerVoiceAvatar(
  participant: { identity?: string | null; kind?: unknown; attributes?: Record<string, string> | null } | null | undefined,
  participants: Iterable<{ identity?: string | null; kind?: unknown; attributes?: Record<string, string> | null }>,
): boolean {
  if (!participant || participant.kind !== AGENT_KIND || participant.identity !== OFFICER_VOICE_AVATAR_IDENTITY) {
    return false;
  }
  const principalIdentity = participant.attributes?.['lk.publish_on_behalf'];
  if (!principalIdentity || principalIdentity === OFFICER_VOICE_AVATAR_IDENTITY) return false;
  for (const candidate of participants) {
    if (candidate?.identity !== principalIdentity) continue;
    if (isAttestedOfficerAssist(candidate) && candidate.attributes?.[OFFICER_AVATAR_STATE_ATTRIBUTE] === OFFICER_AVATAR_ACTIVE) {
      return true;
    }
  }
  return false;
}

type AvatarParticipant = { identity?: string | null; kind?: unknown; attributes?: Record<string, string> | null };

/**
 * The officer seat identity the HUMAN officer's avatar stands in for right now, or null (Q-1724). THE one classifier for
 * the human avatar (Standards X1); the views never classify it themselves.
 *
 * True only when LiveKit attests all of it: kind AGENT; the exact identity; `lk.publish_on_behalf` (minted into the
 * avatar's token by officer-assist) names a PRESENT attested officer-assist whose OWN `human_officer_avatar` attribute is
 * `active`; and whose `human_officer_avatar_for` names a server-minted officer seat that is PRESENT as a STANDARD
 * participant. An orphan, an inactive avatar, a self-claim or a seat that has left is null. Never read from the avatar's
 * own token, name or metadata. Same contract as agent-backend `is_human_officer_avatar` and agent-caller's copy, pinned by
 * agent-backend/demo/utils/human_officer_avatar.v1.json.
 */
export function humanOfficerAvatarVoicedSeat(
  participant: AvatarParticipant | null | undefined,
  participants: Iterable<AvatarParticipant>,
): string | null {
  if (!participant || participant.kind !== AGENT_KIND || participant.identity !== HUMAN_OFFICER_AVATAR_IDENTITY) {
    return null;
  }
  const principalIdentity = participant.attributes?.['lk.publish_on_behalf'];
  if (!principalIdentity || principalIdentity === HUMAN_OFFICER_AVATAR_IDENTITY) return null;
  const seats = Array.from(participants);
  for (const candidate of seats) {
    if (candidate?.identity !== principalIdentity || !isAttestedOfficerAssist(candidate)) continue;
    if (candidate.attributes?.[HUMAN_OFFICER_AVATAR_STATE_ATTRIBUTE] !== HUMAN_OFFICER_AVATAR_ACTIVE) continue;
    const seat = candidate.attributes?.[HUMAN_OFFICER_AVATAR_FOR_ATTRIBUTE];
    if (typeof seat !== 'string' || serverMintedRole(seat) !== 'officer') continue;
    if (seats.some((other) => other?.identity === seat && other.kind === STANDARD_KIND)) return seat;
  }
  return null;
}

/**
 * Is the human officer's avatar carrying an utterance right now (Q-1724)? officer-assist, the avatar's attested principal, sets
 * its own `human_officer_avatar_utt` before the first byte of an utterance and clears it after the avatar's playout tail (or at
 * once on any stop). Listeners route on this signal, never on their own voice detection. False for anything that is not a valid
 * human officer avatar.
 */
export function humanOfficerAvatarCarry(
  participant: AvatarParticipant | null | undefined,
  participants: Iterable<AvatarParticipant>,
): boolean {
  const seats = Array.from(participants);
  if (humanOfficerAvatarVoicedSeat(participant, seats) === null) return false;
  const principal = participant?.attributes?.['lk.publish_on_behalf'];
  const officerAssist = seats.find((candidate) => candidate?.identity === principal);
  return Boolean(officerAssist?.attributes?.[HUMAN_OFFICER_AVATAR_UTTERANCE_ATTRIBUTE]);
}

/** Is this the human officer's bitHuman avatar, carrying that officer's microphone right now (Q-1724)? Not a role. */
export function isHumanOfficerAvatar(
  participant: AvatarParticipant | null | undefined,
  participants: Iterable<AvatarParticipant>,
): boolean {
  return humanOfficerAvatarVoicedSeat(participant, participants) !== null;
}

/**
 * THE SIMULATED (AI) CALLER (Andrew 2026-10-22: "the fake AI caller should show as a simulated caller, but be treated as a
 * caller across the board"). It is a CALLER everywhere (People panel, Participants tab, counts, tiles, transcript labels, room
 * controls, analysis, review) and is only LABELLED simulated. The one classifier is `participantSeatClass`; no screen decides
 * it for itself. The agent-backend (`utils/trusted_participant.py is_simulated_caller`) and agent-caller
 * (`participant_roles.py is_simulated_caller`) copies have the same contract.
 */
export const SIMULATED_CALLER_LABEL = 'Simulated caller';
/** The dispatch name LiveKit stamps in `lk.agent.name` for the agent-caller worker (agent-caller/agent_config.py AGENT_NAME). */
export const AGENT_CALLER_DISPATCH_NAME = 'agent-caller';

/**
 * The dispatch name LiveKit stamps in `lk.agent.name` of a dispatched agent, or '' (an attested attribute: the server
 * writes it, a participant cannot). The one reader outside `isAttestedOfficerAssist`, so the role-derivation ratchet sees no
 * attribute read in the roster.
 */
export function agentDispatchName(participant?: { attributes?: Record<string, string> | null } | null): string {
  return participant?.attributes?.['lk.agent.name'] ?? '';
}

/** What our own agent-caller worker writes about itself: its metadata (`type: ai-caller`) or its dispatch name. */
export function hasSimulatedCallerMarkers(
  participant?: { metadata?: string | null; attributes?: Record<string, string> | null } | null,
): boolean {
  if (!participant) return false;
  if (participant.attributes?.['lk.agent.name'] === AGENT_CALLER_DISPATCH_NAME) return true;
  const raw = (participant.metadata || '').toLowerCase();
  if (
    raw.includes('"type":"ai-caller"') ||
    raw.includes('"type": "ai-caller"') ||
    raw.includes('"agent_name":"agent-caller"') ||
    raw.includes('"agent_name": "agent-caller"')
  ) {
    return true;
  }
  const parsed = parseParticipantMetadata(participant.metadata);
  return (
    parsed?.type === 'ai-caller' ||
    parsed?.agentType === 'ai-caller' ||
    parsed?.agentType === 'agent-caller' ||
    parsed?.agent_name === 'agent-caller'
  );
}

/** The caller avatar renderer (bitHuman) voices the AI caller; it is not the AI caller's own seat. */
function isCallerAvatarIdentity(identity?: string | null): boolean {
  const lowered = (identity || '').toLowerCase();
  return lowered.startsWith('bithuman-') || lowered.startsWith('bh-');
}

/**
 * Is this the simulated caller's OWN seat? Only a dispatched AGENT can be (kind is server-assigned), carrying what our
 * agent-caller worker writes. A person can write anything into their own metadata, so a STANDARD or SIP seat never is; neither
 * is officer-assist (the officer side wins), any avatar renderer (the caller's bitHuman, the officer avatars) or the egress
 * recorder. Metadata on an AGENT is read because our own worker writes it.
 */
export function isSimulatedCallerAgent(
  participant?: { identity?: string | null; kind?: unknown; metadata?: string | null; attributes?: Record<string, string> | null } | null,
): boolean {
  if (!participant || participant.kind !== AGENT_KIND) return false;
  if (isAttestedOfficerAssist(participant)) return false;
  if (isOfficerAvatarAgent(participant) || isCallerAvatarIdentity(participant.identity)) return false;
  return hasSimulatedCallerMarkers(participant);
}

/** Which group of the call a participant belongs to. One answer for every screen. */
export type SeatClass = 'officer' | 'caller' | 'simulated_caller' | 'assistant' | 'unknown';

/**
 * The seat class of a LIVE participant, from what the server attests only (never a display name):
 *   - a phone leg, or a browser seat minted `__cit_`: `caller`; `__off_`: `officer`;
 *   - the simulated caller's own seat: `simulated_caller` (a caller, labelled simulated);
 *   - every other non-STANDARD kind (transcriber, officer-assist, the avatars, the recorder): `assistant`;
 *   - a STANDARD seat with no server-minted suffix: `unknown` (shown unverified, never trusted).
 * A record with no kind falls back to the minted suffix, like `getParticipantRole`.
 */
export function participantSeatClass(participant: RoleParticipant): SeatClass {
  const kind = participant.kind;
  if (typeof kind === 'number' && kind !== STANDARD_KIND) {
    if (kind === SIP_KIND) return 'caller';
    if (isSimulatedCallerAgent(participant)) return 'simulated_caller';
    return 'assistant';
  }
  return serverMintedRole(participant.identity) ?? 'unknown';
}

const SEAT_CLASS_LABEL: Record<SeatClass, string> = {
  officer: 'Officer',
  caller: 'Caller',
  simulated_caller: SIMULATED_CALLER_LABEL,
  assistant: 'Assistant',
  unknown: 'Unverified',
};

export function seatClassLabel(seatClass: SeatClass): string {
  return SEAT_CLASS_LABEL[seatClass];
}

/**
 * The participant's role from what the server attests, and nothing else (Q-153 r6 / Q-765; Prime's ruling: "role
 * derived only from the last-segment identity suffix + participant kind; never from metadata role or display name").
 *
 *   1. LiveKit's `kind` first: SIP is the caller; an AGENT is an officer when its attested dispatch name is
 *      officer-assist, the caller when it is the AI caller or its avatar, otherwise unknown; any other known
 *      non-STANDARD kind is unknown;
 *   2. a STANDARD seat, or a stored record with no kind: the server-minted identity suffix;
 *   3. else unknown.
 *
 * A display name such as "Officer Kelly" or "Andrew Kelly (AI)" therefore decides nothing. Callers that hold a LiveKit
 * participant should pass it whole (or its `kind` and `attributes`) so an agent can be classified.
 */
export function getParticipantRole(participant: RoleParticipant): ParticipantRole {
  // LiveKit's kind decides FIRST (review r9): a suffix is connection-details' mark on a STANDARD seat, so another
  // issuer's SIP leg named `Caller__off_x` or an agent named `agent__cit_x` never borrows it. The same boundary as
  // agent-backend `participant_role_from_attestation`.
  const kind = participant.kind;
  if (typeof kind === 'number' && kind !== STANDARD_KIND) {
    if (kind === SIP_KIND) return 'caller';
    if (kind === AGENT_KIND) {
      if (isAttestedOfficerAssist(participant)) return 'officer';
      const identity = (participant.identity || '').toLowerCase();
      if (identity.startsWith('bithuman-') || identity.startsWith('bh-')) return 'caller';
      if (isSimulatedCallerAgent(participant)) return 'caller';
    }
    return 'unknown';
  }
  // A STANDARD seat, or a stored record with no kind: the server-minted suffix.
  return serverMintedRole(participant.identity) ?? 'unknown';
}

/**
 * The role of a STORED identity string (an archive row, a transcript's participantIdentity, a caption speaker) when no
 * LiveKit participant is at hand: the server-minted suffix, else unknown. Distinct from getParticipantRole so a site
 * cannot silently drop a live participant's kind; a display name passed here is simply unknown.
 */
export function roleFromIdentity(identity?: string | null): ParticipantRole {
  return serverMintedRole(identity) ?? 'unknown';
}

/**
 * The role of a persisted transcript row: the row's server-minted identity suffix for a browser seat, else the
 * transcriber's `is_officer` flag (the backend attests it per segment; this is how officer-assist, an agent with
 * no suffix, is known). Never the speaker name.
 */
export function roleFromTranscript(row: {
  isOfficer?: unknown;
  is_officer?: unknown;
  participantIdentity?: unknown;
  participant_identity?: unknown;
}): ParticipantRole {
  const identity = row.participantIdentity ?? row.participant_identity;
  const minted = serverMintedRole(typeof identity === 'string' ? identity : null);
  if (minted) return minted; // a browser seat: the server-minted suffix decides
  return row.isOfficer === true || row.is_officer === true ? 'officer' : 'unknown';
}

/**
 * Who spoke a transcript segment, for the officer's own monitors (recording consent, goodbye): the server-minted identity
 * or the backend's attested `is_officer` flag (`roleFromTranscript`), or the local seat itself. Those monitors render only
 * in the officer's view (Conference `showRightPanel = isOfficer`), so `isSelf` is the officer. Never the display name: a
 * caller who types "Officer Bob" must not have their consent read as the officer's question (Q-153 r8, audit N1/N2).
 */
export function isOfficerTranscriptSpeaker(row: {
  isOfficer?: unknown;
  is_officer?: unknown;
  participantIdentity?: unknown;
  participant_identity?: unknown;
  isSelf?: unknown;
}): boolean {
  return roleFromTranscript(row) === 'officer' || row.isSelf === true;
}

/**
 * A live-transcript bubble's role (one speaker's consecutive segments): the officer when any of its segments is, by the
 * signed identity or the transcriber's attested flag. Never the display name, so "Bob (AI)" typed by a caller stays a
 * caller (Q-153 r8, audit B12).
 */
export function isOfficerTranscriptBubble(
  messages: ReadonlyArray<Parameters<typeof roleFromTranscript>[0]>,
): boolean {
  return messages.some((message) => roleFromTranscript(message) === 'officer');
}

export function isOfficerParticipant(args: Parameters<typeof getParticipantRole>[0]): boolean {
  return getParticipantRole(args) === 'officer';
}

export function isCallerParticipant(args: Parameters<typeof getParticipantRole>[0]): boolean {
  return getParticipantRole(args) === 'caller';
}

/**
 * Is this participant the officer-assist TTS agent? ONLY from what LiveKit attests (kind AGENT + the dispatch name in
 * `lk.agent.name`); identity text and metadata are never read, because a caller's typed name and `callerInfo` both reach
 * them (Q-153 r7, security R1). Pass the participant itself: with no kind/attributes the answer is false.
 */
export function isOfficerAssistParticipant(
  participant?: { kind?: unknown; attributes?: Record<string, string> | null } | null,
): boolean {
  return isAttestedOfficerAssist(participant);
}

/**
 * #632 — "did a trusted officer send this?": true only for a browser seat whose server-minted suffix is the officer's.
 *
 * `connection-details` mints `${participantName}__${rolePrefix}_${postfix}` with `rolePrefix = isOfficer ? 'off' : 'cit'`
 * computed from the person's DB roles, and the identity is fixed in the signed token. Only the segment after the LAST
 * `__` is the server's (see `serverMintedRole`): a person who typed `evil__off_x` gets `evil__off_x__cit_abc`, a caller.
 * This is the same parser `getParticipantRole` uses; agents are never "trusted officers" here (see isTrustedAgentSender).
 *
 * Fails closed on anything it does not understand.
 */
export function isTrustedOfficerIdentity(identity?: string | null): boolean {
  return serverMintedRole(identity) === 'officer';
}

/**
 * The trust check for a PARTICIPANT (Q-153 r8, security F2): LiveKit's kind first, so only a STANDARD seat (or a kind-less
 * stored record) can be a trusted officer by its server-minted suffix. A SIP leg or an agent never borrows the suffix.
 * Use this, not `isTrustedOfficerIdentity`, wherever the participant object is at hand.
 */
export function isTrustedOfficerParticipant(participant?: { identity?: string | null; kind?: unknown } | null): boolean {
  if (!participant) return false;
  if (typeof participant.kind === 'number' && participant.kind !== STANDARD_KIND) return false;
  return isTrustedOfficerIdentity(participant.identity);
}

/**
 * #632 — the agent-side counterpart of `isTrustedOfficerIdentity`.
 *
 * Several receive paths apply state whose legitimate sender is a dispatched backend
 * agent (officer-assist), not a human officer — `auto_response_status`, officer-speech
 * playout confirmations, and the action-state text streams. Gating those on `__off_`
 * would reject the real sender, so they need their own primitive.
 *
 * `isOfficerAssistParticipant` (attested: kind AGENT + the officer-assist dispatch name, Q-153 r7) answers "is this
 * officer-assist specifically"; this primitive answers the broader "did ANY dispatched agent send it".
 *
 * What IS unforgeable is the participant KIND. LiveKit assigns it server-side from how
 * the participant entered the room: humans minted through `connection-details` are
 * `STANDARD`; agents brought in with `AgentDispatchClient.createDispatch()` are `AGENT`.
 * Nothing a participant sends can change their own kind, so this is the strongest
 * discriminator available on the client.
 *
 * Scope, stated honestly: this proves "a dispatched agent sent it", not "officer-assist
 * specifically sent it". That closes the reported threat — a caller or other in-room
 * participant publishing on backend control topics — because no human participant can
 * ever be `AGENT`. To tell WHICH agent, use the dispatch name LiveKit stamps in
 * `lk.agent.name` (e.g. `isOfficerAssistParticipant` / `isAttestedOfficerAssist`), never
 * the identity text or metadata.
 *
 * Fails closed: an undefined participant, an undefined kind, or any non-AGENT kind is
 * rejected.
 */
export function isTrustedAgentSender(participant?: { kind?: unknown } | null): boolean {
  if (!participant) return false;
  return participant.kind === AGENT_KIND;
}

/**
 * Is this participant an AI agent — any agent (transcriber, AI caller, avatar, officer-assist)?
 * Which agent it is stays a separate question (aiCallerDetection.ts, isOfficerAssistParticipant).
 *
 * The participant KIND decides whenever it is known: AGENT (4) is assigned server-side and
 * cannot be forged (see isTrustedAgentSender), and any other known kind is not an agent. Only
 * when there is no kind (a bare identity from a webhook, a text stream or a stored message)
 * does the identity pattern decide: LiveKit names dispatched agents `agent-<jobId>`, and avatar
 * workers join as `<vendor>-avatar-agent`. An identity that merely CONTAINS "agent" — an officer
 * named `agentsmith` — is a human, and so is any human-minted `name__role_id` identity (Q-436).
 *
 * This routes and labels; it does not decide trust (use isTrustedAgentSender for that).
 */
export function isAgentParticipant(
  participant: string | { identity?: string | null; kind?: unknown } | null | undefined,
): boolean {
  if (!participant) return false;
  if (typeof participant !== 'string' && typeof participant.kind === 'number') {
    return participant.kind === AGENT_KIND;
  }
  const identity = typeof participant === 'string' ? participant : participant.identity ?? '';
  // A human's identity is minted as `<display name>__<role>_<id>` (connection-details), so its first part is a
  // name the person chose: `agent-bob__cit_ab12` or `me-avatar-agent__cit_ab12` is a person. Agents carry no `__`.
  if (identity.includes('__')) return false;
  // Anchored (Q-153 r8, audit B2): an avatar worker is exactly `<vendor>-avatar-agent`; a phone caller's own
  // `sip_x-avatar-agent` is a person.
  return identity.startsWith('agent-') || /^[a-z0-9]+-avatar-agent$/.test(identity);
}
